import { QuestionAnswerMap } from '@care-portals/api-interfaces';

const questionModel = [
  {
    field: 'travelKit_initialConsent',
    label: 'Emergency Travel Kit - Important Information',
    type: 'select',
    educationalSlide:
      'This emergency travel kit is intended only as a backup supply for situations where access to medical care may be limited, such as remote travel, international travel, or unexpected disruptions in the healthcare system. It is not intended for patients who are currently experiencing symptoms, planning to take any of the medications immediately upon arrival, or seeking active treatment for a current illness—if any of those apply, this encounter is not appropriate and you should seek in-person medical evaluation instead.\n\nBy continuing, you acknowledge that you are requesting this kit for emergency preparation only, and that you understand it is not designed for routine use, preventive treatment, management of ongoing symptoms, or substitution for real-time medical care. We are happy to provide this kit when used correctly for travel preparedness or remote access planning, but safe use requires that you only take medications if instructed and only in circumstances where medical care is unavailable.\n\nBy proceeding, you confirm that you understand these limitations and the intended use of this emergency travel kit.',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I have read the information and do NOT wish to proceed',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read and understand the above information, I do NOT have symptoms and I am seeking this kit for travel or emergency back-up supply.',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },

  {
    field: 'travelKit_sex',
    label: 'What was your sex assigned at birth?',
    type: 'select',
    extras: { sendToBeluga: false },
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Male',
          key: 'male'
        },
        {
          value: 'Female',
          key: 'female'
        }
      ]
    }
  },
  {
    field: 'travelKit_pregnancyStatus',
    label:
      'Are you currently pregnant, breastfeeding or planning to become pregnant?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Yes',
          key: 'yes'
        },
        {
          value: 'No',
          key: 'no'
        }
      ]
    }
  },
  {
    field: 'travelKit_consentPregnancy',
    label: 'Consent (pregnancy):',
    type: 'select',
    educationalSlide:
      "I understand that the medication prescribed to me by my healthcare provider may not be safe to take during pregnancy. I acknowledge that taking this medication while pregnant could pose risks to my health and the health of a developing fetus. I understand that I should not take any of the medications within this kit if I'm attempting to become pregnant, am pregnant, or breastfeeding.\n\nBy selecting below, I confirm that I have read and understand the information provided above. I consent to proceed with the treatment under these conditions.",
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I have read the information and do NOT wish to proceed',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read and understand the above information, I understand the risks and wish to proceed',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'travelKit_height',
    label: 'What is your height in feet and inches?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 1,
      name: 'travelKit_height'
    }
  },
  {
    field: 'travelKit_weight',
    label: 'What is your weight in pounds?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 1,
      name: 'travelKit_weight'
    }
  },
  {
    field: 'travelKit_recentCheckup',
    label: 'Have you had a general health checkup in the last 2 years?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Yes',
          key: 'yes'
        },
        {
          value: 'No',
          key: 'no'
        }
      ]
    }
  },
  {
    field: 'travelKit_hasPcp',
    label: 'Do you have a primary care physician?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Yes',
          key: 'yes'
        },
        {
          value: 'No',
          key: 'no'
        }
      ]
    }
  },
  {
    field: 'travelKit_altitudeSickness',
    label:
      'Are you traveling above 8,000 feet in elevation or seeking therapy for altitude sickness?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Yes',
          key: 'yes'
        },
        {
          value: 'No',
          key: 'no'
        }
      ]
    }
  },
  {
    field: 'travelKit_malariaProphylaxis',
    label:
      'Are you traveling to a malaria prone area and are you requesting medications for malaria prophylaxis?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Yes',
          key: 'yes'
        },
        {
          value: 'No',
          key: 'no'
        }
      ]
    }
  },
  {
    field: 'travelKit_kidneyLiverFailure',
    label:
      'Have you ever been diagnosed with advanced kidney failure (CKD stage 4 or 5) or liver cirrhosis?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Yes',
          key: 'yes'
        },
        {
          value: 'No',
          key: 'no'
        }
      ]
    }
  },
  {
    field: 'travelKit_medicationAllergies',
    label:
      'Please indicate if you have allergies to any of the medications listed:',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'Cephalosporins (example: Keflex, cephalexin)',
          key: 'cephalosporins'
        },
        {
          value: 'Penicillins (example: Amoxicillin)',
          key: 'penicillins'
        },
        {
          value: 'Fluoroquinolones (example: Ciprofloxacin, Levofloxacin)',
          key: 'fluoroquinolones'
        },
        {
          value: 'Macrolides (example: Azithromycin (Z-packs), erythromycin)',
          key: 'macrolides'
        },
        {
          value: 'Metronidazole (example: Flagyl)',
          key: 'metronidazole'
        },
        {
          value: 'Sulfonamides (example: Sulfa, Bactrim)',
          key: 'sulfonamides'
        },
        {
          value: 'Tetracyclines (example: Doxycycline)',
          key: 'tetracyclines'
        },
        {
          value: 'Clindamycin',
          key: 'clindamycin'
        },
        {
          value: 'None of the above',
          key: 'none',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    field: 'travelKit_additionalInfo',
    label: 'What other information or questions do you have for the doctor?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'travelKit_additionalInfo'
    }
  },
  {
    field: 'travelKit_consentTruthfulness',
    label: 'Consent (Truthfulness):',
    type: 'select',
    educationalSlide:
      'Please attest to the following confirming that all information you have provided to us is true and complete.\n\nI verify that I am the patient and that I have answered the questions asked in this intake form. I confirm that I have reviewed and understood all the questions asked of me. I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor. I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not, in the information that I provided.',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read the above information and I do consent and wish to move forward',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'travelKit_consentEmergencyUse',
    label: 'Consent (Emergency Use Medication Pack):',
    type: 'select',
    educationalSlide:
      'You are being provided with a medication kit that may include antibiotics or other symptomatic treatment medications for emergency use only. These medications are intended for situations where immediate medical care may not be accessible or available. By accepting this medication pack, you acknowledge and agree to the following:\n\nIntended Use:\nThe medications provided are for emergency use in situations where:\n• You are traveling to areas where standard healthcare may not be accessible.\n• There is a disruption in the healthcare supply chain, limiting access to necessary treatment.\n• You are unable to obtain timely healthcare in urgent or emergency situations.\n• You have consulted your medical provider and received guidance on appropriate use.\n\nNot for Routine or Preventive Use:\nThese medications are not intended for routine, preventive, or self-diagnosed treatment without medical consultation, except in situations where medical care is impossible to obtain.\n\nMost Infections Are Viral:\nYou understand that most common infections are viral in nature and will resolve on their own without the use of antibiotics. Antibiotics are ineffective against viral infections and should not be used unless there is a clear indication of a bacterial infection.\n\nRisks of Inappropriate Use:\nInappropriate use of antibiotics or other medications can result in:\n• Treatment failure or delayed recovery\n• Adverse side effects or allergic reactions\n• The development of antibiotic resistance, making future infections harder to treat\n\nYou agree to follow all written instructions provided with your medication pack and seek medical advice when possible before using any medication.\n\nProper Use and Responsibility:\nYou agree to use the medications only as directed and only in emergency situations as described above.\n\nStorage and Safety:\nYou agree to store the medication pack securely and out of reach of children or unauthorized persons. You will dispose of unused or expired medications safely, following FDA guidelines or through a drug take-back program.\n\nAcknowledgment:\nYou understand that the purpose of this medication pack is to provide a backup option in emergency or extraordinary circumstances only. You confirm that you understand the risks of improper antibiotic use, including antibiotic resistance. You accept full responsibility for the proper and safe use of these medications in accordance with this consent.\n\nBy accepting this medication pack, you confirm that you have read, understood, and agree to the conditions outlined above.',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read the above information and I do consent and wish to move forward',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'travelKit_complete',
    isLast: true,
    label: `Thank you for completing this questionnaire, please select "Finish" to submit`,
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    class: 'ineligible',
    field: 'travelKit_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    extras: { sendToBeluga: false },
    answer: {},
    blockQuestionFlow: true,
    type: 'info',
    isLast: true
  }
] as const;

export default questionModel;

const _type = questionModel;
export type TravelKitQuestionFieldAnswers = Partial<
  QuestionAnswerMap<typeof _type>
>;
